// BLOCO DE IDENTIFICACAO DO CLIENTE — a trava do Enviar ao Caixa ([DA-697], 09/09/2026).
//
// Pedido dele: *"gostaria de travar o envio da venda ao caixa... se nao for assim nao vamos
// conseguir captar os clientes que compram na loja, principalmente os novos"*. Medido em 30 dias:
// 477 das 818 vendas (~17,7 por dia, ticket R$ 61,20) foram ao caixa sem cliente com telefone,
// sem captura e sem recusa — metade da loja passava sem deixar rastro de quem comprou.
//
// Sao TRES saidas, decididas por ele, e o bloco mostra as tres:
//   1. cliente do cadastro **com telefone na ficha** (ficha vazia pede o numero aqui mesmo —
//      17 vendas em 30 dias foram pra fichas sem numero nenhum, e 83 dos 297 clientes estao assim,
//      entao "escolher o cliente" sozinho satisfaria a trava sem capturar contato algum);
//   2. telefone novo, que vira CONTATO da inbox Atendimento — de onde os flys saem (decisao dele);
//   3. "nao quis informar" (decisao dele: *"se o usuario clicar de mais neste botao, a estatistica
//      vai comecar a mostrar e ele vai ter que justificar o por que"*). ⛔ Deixou de ser um clique
//      em 03/10/2026 ([DA-780]): agora pede o NOME e a ETIQUETA, os dois obrigatorios, pra o placar
//      dizer quem recusou.
//
// ⛔ **Nao e' um segundo modal.** O "Enviar ao Caixa" ja abria o `ModalEnviarCaixa`; este bloco
// mora DENTRO dele, no topo, e **so' aparece quando falta** — nas 341 vendas de 30 dias que ja
// tinham cliente com numero, a tela fica exatamente como era. Duas caixas em sequencia foi o
// defeito que a Agenda pagou em 08/09 ([DA-684]).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Phone, Search, UserPlus, Users, Minus, Plus, Check, X, AlertTriangle, IdCard } from 'lucide-react';
import { api } from '../../lib/api';
import { nomeClienteAoDigitar } from '@fiodecobre/shared';
import { Avatar } from '../Avatar';
import { LightboxImagem } from '../../modules/atendimento/componentes/LightboxImagem';
import type { Etiqueta } from '../../modules/atendimento/lib/etiquetas-context';
import { ESTADO_VAZIO, ETIQUETA_QUE_ABRE_A_LISTA, marcadasNaTela, payloadDoCapturar, type EstadoEtiquetas } from './etiquetas-do-balcao';

interface Compra { numero: string; quando: string; total: string; itens: number }

export interface ClienteCapturado { id: number; nome: string; codigo_legado: string | null; consignacao?: boolean }

/** O que a base sabe sobre o numero digitado — as tres perguntas dele, numa resposta ([DA-698]). */
interface Identificacao {
  whatsapp: { existe: boolean; incerto: boolean; motivo?: string };
  contato: { id: number; nome: string; nome_conhecido: string | null; foto_url: string | null } | null;
  cliente: ClienteCapturado | null;
  cliente_origem: 'vinculo' | 'telefone' | null;
  /** O que este numero JA carrega ([DA-722]) — vazio com contato e' medicao, nao "nao sei". */
  etiqueta_ids?: number[];
}

/**
 * Uma conversa recente do Atendimento (`/api/conversas/recentes`). O trecho da ultima mensagem
 * (`com_previa=1`) deixou de ser pedido em 02/11/2026, quando a linha dele saiu da lista.
 *
 * Pedido dele em 09/09/2026: *"ao clicar no campo, exibir a lista do Whatsapp com as ultimas
 * mensagens, pode ser que o cliente tenha enviado mensagem recentemente"*. Medido no mesmo dia:
 * **29 conversas nas ultimas 24h** e 76 na semana — quem esta no balcao agora costuma estar no
 * topo dessa lista.
 */
interface Recente {
  contato_id: number;
  nome: string;
  push_name: string | null;
  telefone: string;
  foto_url: string | null;
  ultima_mensagem_em: string;
}

/** "agora", "14:32", "ontem 09:10", "05/09" — o bastante pra reconhecer quem acabou de escrever. */
function quandoCurto(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const agora = new Date();
  const min = Math.floor((agora.getTime() - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `ha ${min} min`;
  const p = (n: number) => String(n).padStart(2, '0');
  if (d.toDateString() === agora.toDateString()) return `${p(d.getHours())}:${p(d.getMinutes())}`;
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return `ontem ${p(d.getHours())}:${p(d.getMinutes())}`;
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}

/**
 * O NUMERO do caminho (1, 2, 3) — 20px, azul marinho, negrito (pedido dele em 12/09/2026).
 *
 * ⚠️ Cor FIXA de proposito: o bloco tem fundo amarelo claro cravado (`bg-amber-50`), nao token de
 * tema, entao um `var(--...)` aqui se moveria sozinho sobre um fundo que nao se move — que e' o
 * par descasado da [DA-678]. `#1e3a8a` e' o mesmo navy que a casa ja usa na barra de total.
 */
function NumeroCaminho({ n }: { n: 1 | 2 | 3 }) {
  return (
    <span
      className="flex-shrink-0 tabular-nums"
      style={{ fontSize: 20, fontWeight: 800, color: '#1e3a8a', lineHeight: 1 }}
    >
      {n}
    </span>
  );
}

/** `(64) 98442-2082` a partir dos digitos, com ou sem DDI. */
function exibirTel(bruto: string): string {
  let d = String(bruto ?? '').replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
}

export interface BlocoIdentificacaoProps {
  orcamentoId: number | null;
  clienteId: number;
  clienteNome: string;
  /** Cliente escolhido mas com a ficha SEM telefone: a saida 1 fica pela metade. */
  clienteSemNumero: boolean;
  /**
   * A ficha nao tem WhatsApp, mas a venda JA pode ir ao caixa ([DA-867], decisao dele em
   * 30/10/2026): o campo aparece so' como oferta, sem trava e sem texto de obrigacao.
   */
  whatsappOpcional?: boolean;
  /** O X na linha do cliente escolhido: volta pra Consumidor (pedido dele, 30/10/2026). */
  onRemoverCliente?: () => void;
  onAbrirBuscaCliente: () => void;
  onCapturou: (contatoId: number, nome: string) => void;
  /** A ficha que a captura achou ou criou: o pedido passa a apontar pra ela ([DA-698]). */
  onClienteResolvido?: (cliente: ClienteCapturado) => void;
  /** A marca ja esta no pedido (a caixinha do cabecalho do orcamento) — falta so' nome e etiqueta. */
  telefoneRecusado?: boolean;
  /** Nome ja gravado numa recusa anterior deste pedido: o campo nasce com ele. */
  recusaNomeInicial?: string | null;
  /** A recusa foi GRAVADA (rota `registrar-recusa`) com este nome e estas etiquetas ([DA-780]). */
  onRecusou: (nome: string, etiquetaIds: number[]) => void;
  // ── As etiquetas vivem NO MODAL desde a [DA-723] ────────────────────────────────────────────
  // A lista virou a coluna do MEIO, e coluna de grid tem que ser irma das outras duas — mas quem
  // GRAVA continua sendo o botao daqui, no mesmo gesto do telefone ([DA-722] p.2).
  /** So' as que podem ser alvo (`paraCampanha`), JA em ordem alfabetica. */
  etiquetas: Etiqueta[];
  etq: EstadoEtiquetas;
  /** O que o numero identificado JA carregava entra por aqui — a coluna do meio so' desenha. */
  onEtqMudou: (proximo: EstadoEtiquetas | ((a: EstadoEtiquetas) => EstadoEtiquetas)) => void;
  /** Avisa a coluna do meio que a etiqueta virou obrigatoria (numero pronto e nada marcado). */
  onExigenciaMudou: (pendente: boolean) => void;
}

const fmtBRL = (v: number | string) =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function dataCurta(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function BlocoIdentificacaoCliente({
  orcamentoId,
  clienteId,
  clienteNome,
  clienteSemNumero,
  whatsappOpcional = false,
  onRemoverCliente,
  onAbrirBuscaCliente,
  onCapturou,
  onClienteResolvido,
  telefoneRecusado = false,
  recusaNomeInicial = null,
  onRecusou,
  etiquetas: etiquetasDoCombo,
  etq,
  onEtqMudou: setEtq,
  onExigenciaMudou,
}: BlocoIdentificacaoProps) {
  const temCliente = clienteId > 1;
  const [tel, setTel] = useState('');
  const [nome, setNome] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // As compras do cliente, atras do "+" (pedido dele em 09/09/2026: *"so' falta criar um botao de
  // '+', para expandir e mostrar quais os orcamentos e o valor"*). Nasce FECHADO e so' busca
  // quando abre — a tela normal nao paga a consulta.
  const [aberto, setAberto] = useState(false);
  const [compras, setCompras] = useState<Compra[] | null>(null);
  const [resumo, setResumo] = useState<{ total: number; itens: number; qtd: number } | null>(null);

  const carregarCompras = useCallback(() => {
    if (!temCliente) return;
    api.get<{ compras: Compra[]; total_periodo: number; itens_periodo: number }>(
      `/api/clientes/${clienteId}/compras`, { query: { dias: 30 } },
    )
      .then((r) => {
        setCompras(r.compras);
        setResumo({ total: Number(r.total_periodo || 0), itens: Number(r.itens_periodo || 0), qtd: r.compras.length });
      })
      .catch(() => { setCompras([]); setResumo(null); });
  }, [clienteId, temCliente]);

  // O RESUMO (quantas compras e quanto) aparece na linha do cliente mesmo fechado — e' o que da'
  // contexto pra quem esta com a pessoa na frente. A lista completa e' que espera o "+".
  useEffect(() => { carregarCompras(); }, [carregarCompras]);

  const digitos = tel.replace(/\D/g, '');

  // ── O que a base sabe sobre este numero ([DA-698]) ──────────────────────────────────────────
  // As tres perguntas dele chegam JUNTAS, numa chamada so', quando o numero completa: tem
  // WhatsApp? ja esta no chat? ja tem ficha de cliente? Tres chamadas separadas fariam a tela
  // responder em tres tempos, e a decisao do operador depende das tres ao mesmo tempo.
  const [ident, setIdent] = useState<Identificacao | null>(null);
  const [conferindo, setConferindo] = useState(false);
  // O nome so' e' pre-preenchido enquanto o operador nao digitou nada — senao a consulta que chega
  // 400ms depois apagaria o que ele acabou de escrever.
  const nomeTocado = useRef(false);
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; nome: string } | null>(null);

  // ── AS ETIQUETAS ([DA-722], regra dele em 12/09/2026; a lista mudou de lugar na [DA-723]) ───
  // Dois conjuntos, e a diferenca entre eles E' a regra: `gravadas` veio do banco e fica TRAVADA
  // (so' admin retira); `novas` e' o que a pessoa marcou agora e ela pode desmarcar a vontade.
  // Travar tambem o gesto em curso faria um clique errado de um segundo virar tarefa pro admin.
  // ⛔ **O estado chega por PROP** desde 12/09: a lista virou a coluna do meio do modal, e a
  // gravacao continua aqui, no mesmo botao do telefone ([DA-722] p.2).
  const etqMarcadas = marcadasNaTela(etq);

  // Qual contato as marcas atuais pertencem. Trocar o numero em cima de chips marcados levaria a
  // escolha de um contato pro outro, calado.
  const contatoDasMarcas = useRef<number | null>(null);

  useEffect(() => {
    if (temCliente) return;
    if (digitos.length < 10) {
      setIdent(null); setConferindo(false);
      setEtq(ESTADO_VAZIO); contatoDasMarcas.current = null;
      return;
    }
    let cancelado = false;
    setConferindo(true);
    const t = setTimeout(() => {
      api.get<Identificacao>(`/api/contatos/identificar?telefone=${encodeURIComponent(digitos)}&inbox=atendimento`)
        .then((r) => {
          if (cancelado) return;
          setIdent(r);
          // ⛔ Numero COMPLETO fecha a lista de contatos ([DA-722], achado no print): ela e' `fixed`
          // e cobre tudo o que vem abaixo do campo Telefone — inclusive o combo de etiquetas, que
          // a pessoa nem veria. Com o numero inteiro digitado ela nao tem mais o que oferecer, e
          // quem escolhe da lista ja a fecha pelo proprio `escolherRecente`.
          setListaAberta(false);
          // "Se existir tras os dados e deixa o operador atualizar o nome se necessario" — o campo
          // nasce com o nome que a casa ja conhece, editavel.
          if (!nomeTocado.current) setNome(nomeClienteAoDigitar(r.contato?.nome_conhecido ?? r.cliente?.nome ?? ''));
          // Numero novo (ou outro contato) zera o que estava marcado; redigitar o MESMO numero
          // preserva, senao conferir o proprio texto apagaria a escolha.
          const idAgora = r.contato?.id ?? null;
          const trocou = contatoDasMarcas.current !== idAgora;
          contatoDasMarcas.current = idAgora;
          setEtq((a) => ({
            gravadas: r.etiqueta_ids ?? [],
            novas: trocou ? [] : a.novas,
            remover: trocou ? [] : a.remover,
          }));
        })
        .catch(() => {
          if (!cancelado) { setIdent(null); setEtq(ESTADO_VAZIO); }
        })
        .finally(() => { if (!cancelado) setConferindo(false); });
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [digitos, temCliente]);

  // ── A lista de quem falou com a loja ([DA-698], pedido dele) ────────────────────────────────
  // ⚠️ Carrega no PRIMEIRO foco, nao na montagem: nas vendas em que a vendedora ja sabe o numero
  // (ou usa outro caminho) a consulta nunca acontece.
  // ⛔ O dropdown e' `fixed` com `getBoundingClientRect`, nunca `absolute`: este bloco vive dentro
  // do miolo rolavel do modal (`overflow-y-auto`), e ali o absolute sai CORTADO — regra da casa.
  const [recentes, setRecentes] = useState<Recente[] | null>(null);
  const [listaAberta, setListaAberta] = useState(false);
  const [idxTeclado, setIdxTeclado] = useState(-1);
  const campoRef = useRef<HTMLDivElement | null>(null);
  const [caixa, setCaixa] = useState<{ left: number; top: number; width: number; acima: boolean } | null>(null);

  const medirCaixa = useCallback(() => {
    const r = campoRef.current?.getBoundingClientRect();
    if (!r) return;
    const alturaMax = 260;
    const cabeAbaixo = window.innerHeight - r.bottom > alturaMax + 12;
    setCaixa({
      left: r.left,
      top: cabeAbaixo ? r.bottom + 4 : Math.max(8, r.top - alturaMax - 4),
      width: Math.max(r.width, 320),
      acima: !cabeAbaixo,
    });
  }, []);

  function abrirLista() {
    // (o sinal que fechava o combo de etiquetas saiu na [DA-723]: a lista virou coluna fixa e nao
    // disputa mais espaco `fixed` com esta.)
    medirCaixa();
    setListaAberta(true);
    setIdxTeclado(-1);
    if (recentes === null) {
      api.get<Recente[]>('/api/conversas/recentes?inbox=atendimento&limit=30')
        .then(setRecentes)
        .catch(() => setRecentes([]));
    }
  }

  // Fecha ao rolar/redimensionar: a caixa foi medida uma vez e ficaria pendurada no lugar errado.
  // ⛔ Menos a rolagem DA PROPRIA LISTA: o `scroll` com captura pega tambem o evento de dentro dela, e
  // a lista fechava na primeira tentativa de rolar (relato dele em 02/11/2026, com print).
  const listaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!listaAberta) return;
    const fechar = () => setListaAberta(false);
    const aoRolar = (e: Event) => {
      if (e.target instanceof Node && listaRef.current?.contains(e.target)) return;
      fechar();
    };
    window.addEventListener('resize', fechar);
    window.addEventListener('scroll', aoRolar, true);
    return () => { window.removeEventListener('resize', fechar); window.removeEventListener('scroll', aoRolar, true); };
  }, [listaAberta]);

  // ⛔ **A procura vai ao SERVIDOR** ([DA-708], relato dele: *"pesquiso os 4 ultimos digitos no
  // chat e ele acha; no Enviar ao Caixa nao"*). O filtro era LOCAL sobre as 30 linhas ja baixadas
  // — 6% das conversas, so' da inbox `atendimento` —, entao o contato do print (que estava em
  // `compras`, e em 154o lugar na recencia) nao tinha como aparecer, com 4 digitos ou com o
  // numero inteiro. ⚠️ Digitar 1 ou 2 caracteres nao busca: traria a base inteira sem ajudar.
  const [achados, setAchados] = useState<Recente[] | null>(null);
  const [procurando, setProcurando] = useState(false);
  // Desde 02/11/2026 a procura tem CAMPO PROPRIO (pedido dele com print: "em baixo tera um campo de
  // pesquisa; abaixo, um botao Novo Cadastro que exibe os campos"). Ate ali era o proprio Telefone
  // que abria a lista — o que se digitava ali era ao mesmo tempo busca e numero a gravar.
  const [busca, setBusca] = useState('');
  const [novoCadastro, setNovoCadastro] = useState(false);
  // ⚠️ A lista serve DOIS campos e le o termo do que esta na tela: no caminho 1 (ficha sem WhatsApp)
  // o proprio campo do numero continua procurando; no caminho 2, a pesquisa.
  const termo = (temCliente ? tel : busca).trim();
  const buscaNoServidor = termo.length >= 3;

  useEffect(() => {
    if (!listaAberta || !buscaNoServidor) { setAchados(null); setProcurando(false); return; }
    let cancelado = false;
    setProcurando(true);
    const t = setTimeout(() => {
      api.get<Recente[]>(`/api/conversas/recentes?q=${encodeURIComponent(termo)}&limit=20`)
        .then((lst) => { if (!cancelado) { setAchados(lst); setProcurando(false); } })
        .catch(() => { if (!cancelado) { setAchados([]); setProcurando(false); } });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [termo, listaAberta, buscaNoServidor]);

  // Com termo curto (1-2 caracteres) o filtro local sobre os recentes ainda ajuda — ali a lista
  // pequena e' a resposta certa, e ir ao servidor traria tudo.
  const filtradas = buscaNoServidor
    ? (achados ?? [])
    : (recentes ?? []).filter((r) => {
      const q = termo.toLowerCase();
      if (!q) return true;
      const soDigitos = q.replace(/\D/g, '');
      if (soDigitos) return r.telefone.replace(/\D/g, '').includes(soDigitos);
      return `${r.nome} ${r.push_name ?? ''}`.toLowerCase().includes(q);
    });

  // ↑/↓ percorrem, Enter escolhe, Esc fecha. ⚠️ O Enter so' e' capturado quando ha uma linha
  // MARCADA: sem isso ele engoliria o Enter de quem digitou o numero na mao e quer gravar.
  function teclasDaLista(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!listaAberta || !filtradas.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdxTeclado((i) => Math.min(i + 1, filtradas.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdxTeclado((i) => Math.max(i - 1, -1)); }
    else if (e.key === 'Enter' && idxTeclado >= 0) { e.preventDefault(); escolherRecente(filtradas[idxTeclado]!); }
    else if (e.key === 'Escape') { e.preventDefault(); setListaAberta(false); }
  }

  function escolherRecente(r: Recente) {
    let d = r.telefone.replace(/\D/g, '');
    if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
    setTel(d);
    setErro(null);
    setListaAberta(false);
    setBusca('');
    // Escolher abre os campos ja preenchidos: o operador confere/ajusta o nome e grava ([DA-698],
    // criterio 2 dele: "traz os dados e deixa o operador atualizar o nome").
    setNovoCadastro(true);
    // O nome vem junto — e' o que o caminho 2 exige. Nome anonimo (o proprio numero) nao preenche.
    const bom = [r.nome, r.push_name].find((n) => n && !/^[+\d\s()-]+$/.test(n.trim()));
    if (bom) { nomeTocado.current = true; setNome(nomeClienteAoDigitar(bom.trim())); }
  }

  // ⛔ Numero que o WhatsApp diz NAO existir barra a gravacao — a lista de flys e' o destino, e
  // numero errado ali fica pra sempre. O estado "nao deu pra conferir" (WhatsApp fora do ar) NAO
  // barra nada: a rota devolve tres estados justamente pra isso ([DA-697] p. do `checar-numero`).
  const semWhatsApp = !!ident && !ident.whatsapp.incerto && !ident.whatsapp.existe;
  const fichaAchada = ident?.cliente ?? null;

  // ⛔ O NOME e' OBRIGATORIO no caminho 2 (decisao dele em 09/09/2026, revendo a de horas antes):
  // *"sem ele nao sabemos o nome do contato, e o telefone fica quase inutilizavel"*. Numero sem
  // dono nao serve nem pro fly nem pra ficha — e a saida de quem nao quer informar e' o caminho 3.
  // ⚠️ NAO vale pro caminho 1 (cliente do cadastro sem telefone): la o nome ja esta na ficha e nao
  // ha campo nenhum pra preencher — por isso `criarCliente` e' parametro, nao constante.
  const nomeCurto = nome.trim().length < 2;

  async function capturar(criarCliente: boolean) {
    if (!orcamentoId || digitos.length < 10 || semWhatsApp) return;
    if (criarCliente && nomeCurto) return;
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.post<{ contato_id: number; contato_nome: string; cliente: ClienteCapturado | null }>(
        `/api/orcamentos/${orcamentoId}/capturar-contato`,
        // `criar_cliente` so' sai do caminho 2: no envio por WhatsApp o numero nao afirma de quem
        // e' a venda ([DA-668] — as vezes o orcamento vai pro engenheiro da obra).
        // ⚠️ As etiquetas vao no MESMO botao ([DA-722]): campo que so' vale se um SEGUNDO botao for
        // apertado e' o defeito que a [DA-707] pagou — o intermediario e' o que se esquece.
        {
          telefone: digitos, nome: nome.trim() || null, criar_cliente: criarCliente,
          ...payloadDoCapturar(etq),
        },
      );
      if (r.cliente) onClienteResolvido?.(r.cliente);
      onCapturou(r.contato_id, r.contato_nome);
    } catch (e: any) {
      // Falha de acao fica NA TELA ate a proxima tentativa, nao num toast de 4s: quem esta no
      // balcao com o cliente na frente nao pode pedir replay do aviso.
      setErro(e?.corpo?.erro ?? e?.message ?? 'Nao deu pra gravar o telefone. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  }

  const rotuloCaminho1 = temCliente ? 'Cliente do cadastro' : 'Cliente ja cadastrado';

  // ── A EXIGENCIA de etiqueta no caminho 2 ([DA-722] p.10) ────────────────────────────────────
  // So' vale com o numero COMPLETO e com WhatsApp: antes disso nao ha contato a etiquetar.
  // ⚠️ **Isto nao decide mais o que APARECE** — desde a [DA-723] a lista e' a coluna do meio e
  // esta sempre a vista; aqui sobrou so' a pergunta "ja da' pra cobrar?".
  const exigeEtiqueta = digitos.length >= 10 && !semWhatsApp && etiquetasDoCombo.length > 0;

  // ⛔ **A ETIQUETA E' OBRIGATORIA no caminho 2** (decisao dele, 12/09/2026: *"o campo Etiqueta deve
  // ser obrigatorio"*). Mesmo padrao do NOME: vale so' aqui, porque so' aqui o numero afirma de quem
  // e' a venda — o bloco "Contato novo" do envio por WhatsApp manda `criar_cliente` ausente e segue
  // sem exigir nada ([DA-668]).
  // ⚠️ Contato que JA carrega etiqueta ja satisfaz (17 dos 21 capturados estao assim): o que se
  // exige e' que o contato TENHA area de atuacao, nao que ela seja marcada de novo toda venda.
  const semEtiqueta = exigeEtiqueta && etqMarcadas.length === 0;

  // ── Caminho 3: a recusa COM nome e etiqueta ([DA-780], decisao dele em 03/10/2026) ──────────
  // *"Eu precisava de mais informacoes neste relatorio. Nome e Etiqueta"* + *"os dois campos
  // obrigatorios"*. Ate aqui a recusa era um clique, e as 14 recusas de outubro chegaram ao placar
  // todas como "Consumidor". ⚠️ A etiqueta e' a MESMA coluna do meio do caminho 2 — uma lista so',
  // nao uma segunda copia dentro deste bloco.
  // ⛔ Um gesto so': o botao "Nao quis informar" grava marca + nome + etiquetas juntos. Campo que
  // so' vale se um segundo botao for apertado e' o defeito da [DA-707].
  const [nomeRecusa, setNomeRecusa] = useState(recusaNomeInicial ?? '');
  const [recusando, setRecusando] = useState(false);
  const nomeRecusaCurto = nomeRecusa.trim().length < 2;
  const recusaSemEtiqueta = etqMarcadas.length === 0;
  // So' cobra a etiqueta do caminho 3 quando a pessoa ja esta NELE (digitou o nome, ou a marca
  // veio do cabecalho) — senao a coluna do meio acenderia o `*` pra quem vai pelo caminho 2.
  const noCaminho3 = nomeRecusa.trim().length > 0 || telefoneRecusado;

  // A `CONSUMIDOR` ja vem marcada ao entrar no caminho 3 (pedido dele em 03/10/2026: *"na Opcao 3
  // deixe pre-selecionado a etiqueta CONSUMIDOR, para facilitar"*). ⚠️ So' no caminho 3: a lista
  // e' a mesma do caminho 2, e la' a marca vira carimbo permanente no contato do WhatsApp.
  // ⚠️ Uma vez por modal e so' com a lista vazia — quem desmarcou, ou ja escolheu outra, decidiu.
  const consumidorSugerida = useRef(false);
  useEffect(() => {
    if (consumidorSugerida.current || !noCaminho3) return;
    const consumidor = etiquetasDoCombo.find((e) => e.nome.toUpperCase() === ETIQUETA_QUE_ABRE_A_LISTA);
    if (!consumidor) return; // lista ainda carregando: tenta de novo quando ela chegar
    consumidorSugerida.current = true;
    setEtq((a) => (marcadasNaTela(a).length ? a : { ...a, novas: [consumidor.id] }));
  }, [noCaminho3, etiquetasDoCombo, setEtq]);

  async function registrarRecusa() {
    if (!orcamentoId || nomeRecusaCurto || recusaSemEtiqueta) return;
    setRecusando(true);
    setErro(null);
    try {
      const r = await api.post<{ recusa_nome: string; recusa_etiqueta_ids: number[] }>(
        `/api/orcamentos/${orcamentoId}/registrar-recusa`,
        { nome: nomeRecusa.trim(), etiqueta_ids: etqMarcadas },
      );
      onRecusou(r.recusa_nome, r.recusa_etiqueta_ids);
    } catch (e: any) {
      setErro(e?.corpo?.erro ?? e?.message ?? 'Nao deu pra registrar. Tente de novo.');
    } finally {
      setRecusando(false);
    }
  }

  // A coluna do meio acende o `*` a partir daqui — a exigencia nasce deste lado (ela depende do
  // telefone, ou do caminho 3) e o sinal precisa estar colado no campo que a resolve.
  const etiquetaPendente = semEtiqueta || (noCaminho3 && recusaSemEtiqueta && etiquetasDoCombo.length > 0);
  useEffect(() => { onExigenciaMudou(etiquetaPendente); }, [etiquetaPendente, onExigenciaMudou]);

  const botaoGravar = (
    <button
      type="button"
      onClick={() => capturar(true)}
      disabled={salvando || digitos.length < 10 || semWhatsApp || nomeCurto || semEtiqueta}
      className="flex-shrink-0 px-3 py-1.5 rounded-lg btn-primario text-sm flex items-center gap-1.5 disabled:opacity-50"
    >
      {salvando ? <Loader2 size={14} className="animate-spin" /> : fichaAchada ? <IdCard size={14} /> : <UserPlus size={14} />}
      {salvando ? 'Gravando...' : fichaAchada ? 'Usar esta ficha' : 'Gravar'}
    </button>
  );

  return (
    // ⚠️ **Altura e' recurso escasso aqui, e foi medido** (09/09/2026, no dia em que subiu): na
    // janela dele (1536x740 CSS, que e' 1920x1030 a 125%) o modal passou a rolar **92px** com este
    // bloco dentro — e ele pediu, com print: *"Atualize a tela para que nao precize rolar"*. O que
    // saiu foram os dois divisores "OU" (60px com os espacos), a segunda linha do aviso do topo e
    // o empilhamento do bloco 2, cujo botao passou pra MESMA linha dos campos.
    // ⛔ **Sem o "OU", a numeracao 1/2/3 passaria a ler como SEQUENCIA** — tres passos a cumprir,
    // e nao tres alternativas. Por isso o aviso do topo diz "basta UM destes tres": a palavra faz
    // o trabalho que os divisores faziam, de graca em altura.
    <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50/60 p-2.5">
      <div className="text-[13px] text-amber-900 mb-2">
        {/* Texto vencido de UI e' instrucao que o operador obedece ([DA-639]): com o cliente sem
            WhatsApp ja liberado ([DA-867]), "para liberar o envio" mandaria procurar uma trava
            que nao existe. */}
        {whatsappOpcional
          ? <><b>Quem esta comprando?</b> Cliente escolhido — a venda ja pode ir ao caixa.</>
          : <><b>Quem esta comprando?</b> Basta <b>um</b> destes tres caminhos para liberar o envio.</>}
      </div>

      {/* ── Caminho 1: cliente do cadastro ─────────────────────────────────────────────── */}
      <div className="rounded-lg border border-gray-300 bg-white p-2.5">
        {/* Com cliente ESCOLHIDO o rotulo fica em cima (a linha do cliente ja e' larga); sem
            cliente, rotulo e botao dividem a MESMA linha — 24px que ninguem sente falta. */}
        {temCliente && (
          <div className="flex items-center gap-2 mb-1.5">
            <NumeroCaminho n={1} />
            <span className="text-[11px] font-bold text-gray-600 uppercase">{rotuloCaminho1}</span>
          </div>
        )}

        {temCliente ? (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-2">
              {/* "+"/"−" das compras. `aria-expanded` porque o rotulo e' um sinal grafico. */}
              <button
                type="button"
                onClick={() => { setAberto((v) => !v); if (!compras) carregarCompras(); }}
                aria-expanded={aberto}
                aria-label={aberto ? 'Recolher as compras' : 'Ver as compras'}
                className="w-[22px] h-[22px] flex-shrink-0 flex items-center justify-center rounded border border-gray-400 bg-white text-gray-700 hover:bg-gray-50"
              >
                {aberto ? <Minus size={13} /> : <Plus size={13} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{clienteNome}</div>
                <div className="text-[11px] text-gray-500">
                  cliente #{clienteId}
                  {resumo && resumo.qtd > 0 && (
                    <> · {resumo.qtd} {resumo.qtd === 1 ? 'compra' : 'compras'} nos ultimos 30 dias · R$ {fmtBRL(resumo.total)}</>
                  )}
                </div>
              </div>
              {/* Escolhido aqui dentro nao tinha volta (pedido dele com print, 30/10/2026). Mesmo X
                  do campo Cliente da tela do orcamento. */}
              {onRemoverCliente && (
                <button
                  type="button"
                  onClick={onRemoverCliente}
                  title="Tirar o cliente deste pedido (volta pra Consumidor)"
                  aria-label="Tirar o cliente deste pedido"
                  className="flex-shrink-0 text-gray-400 hover:text-red-600 transition"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* ⛔ O campo que TRAVA a venda vem ANTES da lista de compras, nao depois: com as
                compras abertas ele cairia na dobra do miolo rolavel e desapareceria — e o que
                depende de rolagem nao existe pra quem opera ([DA-655]). */}
            {clienteSemNumero && (
              <div className="mt-2.5 rounded-lg border border-orange-300 bg-orange-50 p-2.5">
                {/* ⚠️ Diz WHATSAPP, nao "telefone" ([DA-698]): desde hoje a ficha que so' tem FIXO
                    tambem cai aqui, e "nao tem telefone" seria mentira na cara de quem ve o fixo
                    cadastrado — texto de UI e' instrucao que o operador obedece. */}
                <div className="text-[12px] font-semibold text-orange-800 mb-1.5">
                  {whatsappOpcional
                    ? 'Este cadastro nao tem WhatsApp. Se o cliente souber, informe — e opcional.'
                    : 'Este cadastro nao tem WhatsApp. Informe agora para continuar.'}
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-[11px] text-gray-600 mb-0.5">{whatsappOpcional ? 'WhatsApp (opcional)' : 'WhatsApp *'}</label>
                    <div className="relative" ref={campoRef}>
                      <Phone size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        value={tel}
                        onChange={(e) => { setTel(e.target.value); setErro(null); if (!listaAberta) abrirLista(); }}
                        onFocus={abrirLista}
                        onKeyDown={teclasDaLista}
                        placeholder="(64) 9 ...."
                        disabled={salvando}
                        className="w-full pl-7 pr-2.5 py-1.5 border border-orange-300 rounded bg-white outline-none focus:ring-2 focus:ring-orange-400 text-sm"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => capturar(false)}
                    disabled={salvando || digitos.length < 10}
                    className="px-3 py-1.5 rounded-lg btn-primario text-sm flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {salvando ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                    Gravar
                  </button>
                </div>
                <div className="text-[11px] text-orange-800 mt-1">
                  Vai para a ficha do {clienteNome.split(' ')[0]} e para esta venda.
                </div>
                {/* Botao desabilitado DIZ por que (convencao da casa). */}
                {digitos.length > 0 && digitos.length < 10 && (
                  <div className="text-[11px] text-gray-600 mt-1">Digite o numero com DDD.</div>
                )}
              </div>
            )}

            {aberto && (
              <div className="mt-2.5 rounded-lg border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-[1fr_105px_90px_52px] px-2.5 py-1.5 text-[10px] font-bold uppercase"
                  style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                  <div>Pedido</div><div>Quando</div><div className="text-right">Valor</div><div className="text-right">Itens</div>
                </div>
                {compras === null && (
                  <div className="px-2.5 py-2 text-[12px] text-gray-500 bg-white">carregando...</div>
                )}
                {compras?.length === 0 && (
                  <div className="px-2.5 py-2 text-[12px] text-gray-500 bg-white">
                    Nenhuma compra nos ultimos 30 dias.
                  </div>
                )}
                {compras?.map((c) => (
                  <div key={c.numero} className="grid grid-cols-[1fr_105px_90px_52px] items-center px-2.5 py-1 border-t border-gray-100 bg-white text-[12px]">
                    <div className="font-mono font-bold text-blue-700">{c.numero}</div>
                    <div className="text-gray-500">{dataCurta(c.quando)}</div>
                    <div className="text-right font-semibold text-gray-800">R$ {fmtBRL(c.total)}</div>
                    <div className="text-right text-gray-500">{c.itens}</div>
                  </div>
                ))}
                {!!compras?.length && resumo && (
                  <div className="grid grid-cols-[1fr_90px_52px] px-2.5 py-1.5 border-t border-gray-300 bg-gray-50 text-[12px] font-bold">
                    <div className="text-gray-600">{resumo.qtd} {resumo.qtd === 1 ? 'compra' : 'compras'}</div>
                    <div className="text-right">R$ {fmtBRL(resumo.total)}</div>
                    <div className="text-right text-gray-500">{resumo.itens}</div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <NumeroCaminho n={1} />
              <span className="text-[11px] font-bold text-gray-600 uppercase">{rotuloCaminho1}</span>
            </div>
            <button
              type="button"
              onClick={onAbrirBuscaCliente}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg border border-gray-500 bg-white text-[13px] font-semibold text-gray-700 hover:bg-gray-100 flex items-center gap-1.5"
            >
              <Users size={14} /> Escolher cliente
            </button>
          </div>
        )}
      </div>

      {/* Caminhos 2 e 3 recolhem quando o cliente ja esta escolhido: ali o caminho ja foi
          decidido, e mante-los em destaque ofereceria tres saidas pra uma pergunta ja respondida. */}
      {!temCliente && (
        <>
          {/* ── Caminho 2: telefone novo vira contato do WhatsApp ────────────────────────── */}
          {/* `mt-4`: o respiro entre os tres caminhos dobrou a pedido dele — com `mt-2` os blocos
              encostavam e os tres liam como um formulario so', nao como tres alternativas. */}
          <div className="mt-4 rounded-lg border border-gray-300 bg-white p-2.5">
            <div className="flex items-center gap-2 mb-1.5">
              <NumeroCaminho n={2} />
              {/* Rotulo "WhatsApp" — pedido dele em 02/11/2026, trocando "Cadastrar o WhatsApp
                  agora — entra na lista de onde saem as promocoes". */}
              <span className="text-[11px] font-bold text-gray-600 uppercase">WhatsApp</span>
            </div>
            {/* A PESQUISA: clicar abre quem falou com a loja, digitar 3+ caracteres procura nas duas
                caixas ([DA-698], [DA-708]). Escolher abre os campos abaixo ja preenchidos. */}
            <div className="relative" ref={campoRef}>
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={busca}
                onChange={(e) => { setBusca(e.target.value); if (!listaAberta) abrirLista(); }}
                onFocus={abrirLista}
                onKeyDown={teclasDaLista}
                placeholder="Procurar contato pelo nome ou telefone..."
                disabled={salvando}
                className="w-full pl-7 pr-2.5 py-1.5 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
            </div>
            {!novoCadastro && (
              <button
                type="button"
                onClick={() => setNovoCadastro(true)}
                className="mt-1.5 px-3 py-1.5 rounded-lg btn-primario text-sm flex items-center gap-1.5"
              >
                <UserPlus size={14} /> Novo Cadastro
              </button>
            )}
            {novoCadastro && (
            // Nome, telefone e o botao na MESMA linha: empilhado, este bloco sozinho custava 153px.
            // ⚠️ Ordem NOME → TELEFONE desde 02/11/2026 (pedido dele: "primeiro o nome, depois o
            // telefone"), com a foto do contato a esquerda do nome.
            <div className="flex items-end gap-2 mt-1.5">
              {/* A foto sai do CONTATO que o numero aponta (`identificar`), nao da linha escolhida
                  na pesquisa: se o operador editar o numero depois de escolher, a foto acompanha o
                  numero que vai ser gravado. 34px = altura dos campos. */}
              {ident?.contato && !conferindo && digitos.length >= 10 && (
                <div className="flex-shrink-0">
                  {/* Clicar amplia (pedido dele em 02/11/2026) — o lightbox do chat, o mesmo do
                      Contatos do Dia. Sem foto (so' iniciais) nao ha o que ampliar. */}
                  <Avatar
                    nome={ident.contato.nome_conhecido || nome || ident.contato.nome}
                    fotoUrl={ident.contato.foto_url}
                    tamanho={34}
                    onFotoClick={ident.contato.foto_url
                      ? () => setFotoAmpliada({ url: ident.contato!.foto_url!, nome: ident.contato!.nome_conhecido || nome || ident.contato!.nome })
                      : undefined}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {/* ⛔ Era caixa livre (`no-upper`) ate 03/10/2026: este nome vira a FICHA do cliente,
                    e o cadastro passou a ser maiusculo, sem acento e sem Ç (pedido dele). */}
                <label className="block text-[11px] text-gray-600 mb-0.5">Nome *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => { nomeTocado.current = true; setNome(nomeClienteAoDigitar(e.target.value)); }}
                  placeholder="Como aparecera na lista"
                  disabled={salvando}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] text-gray-600 mb-0.5">Telefone *</label>
                <div className="relative">
                  <Phone size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={tel}
                    onChange={(e) => { setTel(e.target.value); setErro(null); }}
                    placeholder="(64) 9 ...."
                    disabled={salvando}
                    className="w-full pl-7 pr-2.5 py-1.5 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  />
                </div>
              </div>
              {/* ⚠️ **O Gravar fica na LINHA dos campos** ([DA-723]): ate 12/09 ele descia pra
                  baixo do combo de etiquetas, porque a secao nascia logo abaixo e ninguem a veria
                  ([DA-654]). Com a lista na coluna do meio, sempre a vista, o motivo caiu — e o
                  gesto natural volta a ser digitar-nome-e-clicar. */}
              {botaoGravar}
            </div>
            )}

            {/* ── O que a casa sabe deste numero ────────────────────────────────────────────────
                UMA linha, sempre no mesmo lugar, trocando de conteudo conforme a resposta chega —
                altura e' recurso escasso neste modal (medida em 09/09: o bloco ja fez o miolo
                rolar 92px). Botao desabilitado DIZ por que, convencao da casa. */}
            {digitos.length > 0 && digitos.length < 10 && (
              <div className="text-[11px] text-gray-600 mt-1">Digite o numero com DDD.</div>
            )}
            {digitos.length >= 10 && conferindo && (
              <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> conferindo o numero...
              </div>
            )}
            {digitos.length >= 10 && !conferindo && ident && (
              <div className="mt-1 text-[11px] flex items-start gap-1.5">
                {semWhatsApp ? (
                  <>
                    <X size={12} className="text-red-600 flex-shrink-0 mt-[1px]" />
                    <span className="text-red-700 font-semibold">
                      Este numero nao tem WhatsApp. Confira os digitos — sem WhatsApp ele nao
                      recebe as promocoes.
                    </span>
                  </>
                ) : ident.whatsapp.incerto ? (
                  <>
                    <AlertTriangle size={12} className="text-amber-600 flex-shrink-0 mt-[1px]" />
                    <span className="text-amber-800">
                      Nao deu pra conferir no WhatsApp agora — da' pra gravar assim mesmo.
                    </span>
                  </>
                ) : (
                  <>
                    <Check size={12} className="text-green-600 flex-shrink-0 mt-[1px]" />
                    <span className="text-gray-700">
                      Tem WhatsApp
                      {ident.contato
                        ? <> · <b>ja esta na sua lista</b>{ident.contato.nome_conhecido ? <> como <b>{ident.contato.nome_conhecido}</b></> : ' (ainda sem nome)'}</>
                        : ' · numero novo'}
                    </span>
                  </>
                )}
              </div>
            )}
            {/* A ficha que ja existe: a venda passa a ser DELA — e' o caminho 1 resolvido sem
                procurar na lista. Linha propria porque muda o que o botao faz. */}
            {digitos.length >= 10 && !conferindo && fichaAchada && (
              <div className="mt-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-900 flex items-center gap-1.5">
                <IdCard size={12} className="flex-shrink-0" />
                <span>
                  Ja tem ficha: <b>{fichaAchada.nome}</b>
                  {fichaAchada.codigo_legado ? ` (cod. ${fichaAchada.codigo_legado})` : ''} — a venda fica
                  neste nome.
                </span>
              </div>
            )}
            {/* Botao desabilitado DIZ por que — e aqui ele fica desabilitado com o numero JA
                completo, que e' o momento em que o operador acha que era so' clicar. */}
            {digitos.length >= 10 && !conferindo && !semWhatsApp && nomeCurto && (
              <div className="text-[11px] text-gray-600 mt-1">
                Falta o <b>nome</b> — sem ele o numero nao serve nem pra lista nem pra ficha.
              </div>
            )}
            {/* Botao desabilitado DIZ por que (convencao da casa) — e aqui ele fica cinza com o
                numero e o nome JA preenchidos, que e' o momento em que o operador acha que era
                so' clicar. */}
            {/* ⚠️ **A recusa aponta a porta que a resolve** ([DA-560]) — e desde a [DA-723] essa
                porta nao esta mais aqui dentro: e' a coluna do meio do modal. */}
            {semEtiqueta && !nomeCurto && !conferindo && (
              <div className="text-[11px] text-gray-600 mt-1">
                Falta a <b>etiqueta</b> — marque a area de atuacao na coluna do meio.
              </div>
            )}
          </div>

          {/* ── Caminho 3: a recusa, com NOME e ETIQUETA ([DA-780]) ─────────────────────────
              Rotulo, campo e botao na MESMA linha, como o caminho 2: altura e' recurso escasso
              neste modal (ja rolou 92px uma vez, [DA-697]). */}
          <div className="mt-4 rounded-lg border border-dashed border-gray-400 bg-gray-50 p-2.5">
            <div className="flex items-center gap-2 mb-1.5">
              <NumeroCaminho n={3} />
              <span className="text-[11px] font-bold text-gray-600 uppercase">
                O cliente nao quis informar o telefone
                <span className="font-normal normal-case text-gray-500"> — fica no seu nome e entra no placar</span>
              </span>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-0">
                <label className="block text-[11px] text-gray-600 mb-0.5">Nome do cliente *</label>
                <input
                  type="text"
                  value={nomeRecusa}
                  onChange={(e) => { setNomeRecusa(nomeClienteAoDigitar(e.target.value)); setErro(null); }}
                  placeholder="Como ele se apresentou"
                  maxLength={80}
                  disabled={recusando}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded bg-white outline-none focus:ring-2 focus:ring-gray-400 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={registrarRecusa}
                disabled={recusando || !orcamentoId || nomeRecusaCurto || recusaSemEtiqueta}
                className="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-lg border border-gray-500 bg-white text-[13px] font-semibold text-gray-700 hover:bg-gray-100 flex items-center gap-1.5 disabled:opacity-50"
              >
                {recusando && <Loader2 size={14} className="animate-spin" />}
                {recusando ? 'Registrando...' : 'Nao quis informar'}
              </button>
            </div>
            {/* Botao desabilitado DIZ por que (convencao da casa) — e so' depois que a pessoa
                entrou neste caminho, senao a linha cobraria quem vai pelo caminho 2. */}
            {noCaminho3 && (nomeRecusaCurto || recusaSemEtiqueta) && (
              <div className="text-[11px] text-gray-600 mt-1">
                {telefoneRecusado && nomeRecusa.trim().length === 0 && <>Marcado no orcamento — </>}
                Falta {nomeRecusaCurto && <>o <b>nome</b></>}
                {nomeRecusaCurto && recusaSemEtiqueta && ' e '}
                {recusaSemEtiqueta && <>a <b>etiqueta</b> (coluna do meio)</>}.
              </div>
            )}
          </div>
        </>
      )}

      {erro && (
        <div className="mt-2.5 rounded-lg border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-700">
          {erro}
        </div>
      )}

      {/* ── Quem falou com a loja ([DA-698]) ──────────────────────────────────────────────────
          ⚠️ `fixed` + medicao: dentro do miolo rolavel do modal, `absolute` sairia cortado.
          ⚠️ `z-[1200]`: acima do proprio modal (z-50) e do `.modal-overlay` da casa (1000).
          ⛔ Fecha por `onMouseDown` no fundo, nunca por `onBlur` do campo — blur dispara ANTES do
          clique na linha e a escolha se perderia. */}
      {listaAberta && caixa && (
        <>
          <div className="fixed inset-0 z-[1190]" onMouseDown={() => setListaAberta(false)} />
          <div
            ref={listaRef}
            className="fixed z-[1200] rounded-lg border border-gray-300 bg-white shadow-xl overflow-y-auto"
            style={{ left: caixa.left, top: caixa.top, width: caixa.width, maxHeight: 260 }}
          >
            <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase text-gray-500 border-b border-gray-200 bg-gray-50 sticky top-0">
              {/* O rotulo diz o ALCANCE: procurando, a lista deixa de ser "quem falou" e passa a
                  cobrir a base inteira — inclusive quem ainda nao conversou ([DA-708]). */}
              {buscaNoServidor ? 'Contatos da sua lista' : 'Quem falou com a loja · mais recentes'}
            </div>
            {(buscaNoServidor ? procurando : recentes === null) && (
              <div className="px-2.5 py-3 text-[12px] text-gray-500 flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" />
                {buscaNoServidor ? 'procurando na sua lista...' : 'carregando conversas...'}
              </div>
            )}
            {/* ⚠️ So' diz "nao achei" DEPOIS da resposta chegar — enquanto procura, o vazio nao
                afirma nada ([DA-649]: selo que acende antes do estado vira resposta ao clique). */}
            {(buscaNoServidor ? (!procurando && achados !== null) : recentes !== null) && filtradas.length === 0 && (
              <div className="px-2.5 py-3 text-[12px] text-gray-500">
                {termo ? 'Nenhum contato com esse numero ou nome. Use o Novo Cadastro.' : 'Nenhuma conversa recente.'}
              </div>
            )}
            {filtradas.map((r, i) => {
              const nomeBom = [r.nome, r.push_name].find((n) => n && !/^[+\d\s()-]+$/.test(n.trim()));
              return (
                <button
                  key={r.contato_id}
                  type="button"
                  // `onMouseDown` (nao `onClick`): o blur do campo fecharia a lista antes do clique.
                  onMouseDown={(e) => { e.preventDefault(); escolherRecente(r); }}
                  onMouseEnter={() => setIdxTeclado(i)}
                  className={`w-full text-left px-2.5 py-0.5 border-b border-gray-100 last:border-0 flex items-center gap-2 ${i === idxTeclado ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                >
                  {/* ⚠️ O `<Avatar>` da casa, nao um `<img>` proprio: a URL do WhatsApp
                      (`pps.whatsapp.net`) EXPIRA, e ele ja cai nas iniciais coloridas quando a foto
                      falha — um `<img>` cru deixaria o quadro quebrado na lista.
                      ⛔ Sem `onFotoClick`: aqui a linha inteira e' o gesto (escolher o contato), e
                      ampliar a foto roubaria o clique de quem so' queria selecionar. */}
                  {/* 44px com `py-0.5`: a linha segue com os 48px de antes (6+36+6), so' trocou
                      respiro por foto — pedido dele em 02/11/2026: "aumentar a foto sem aumentar a
                      altura da linha", e depois "o maximo que der". */}
                  <Avatar nome={nomeBom || exibirTel(r.telefone)} fotoUrl={r.foto_url} tamanho={44} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      {/* Telefone NA FRENTE do nome e na mesma cor (pedido dele em 02/11/2026, com
                          print): saiu da segunda linha, onde era cinza e pequeno. So' o nome trunca. */}
                      <span className="min-w-0 flex items-baseline gap-2 text-[13px] font-semibold text-gray-800">
                        <span className="truncate">{nomeBom || exibirTel(r.telefone)}</span>
                        {nomeBom && <span className="flex-shrink-0">{exibirTel(r.telefone)}</span>}
                      </span>
                      <span className="text-[10px] text-gray-500 flex-shrink-0">{quandoCurto(r.ultima_mensagem_em)}</span>
                    </div>
                    {/* (A linha com o trecho da ultima mensagem — "nos: ..." / "ele: ..." — saiu em
                        02/11/2026, pedido dele com print.) */}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Portal pro `<body>` em z 9999, acima do modal; o Esc dele e' capturado antes e nao fecha
          o Enviar ao Caixa junto. */}
      {fotoAmpliada && (
        <LightboxImagem
          imagens={[{ url: fotoAmpliada.url, legenda: fotoAmpliada.nome }]}
          onFechar={() => setFotoAmpliada(null)}
        />
      )}
    </div>
  );
}
